import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, Server, Shield, AlertTriangle, Loader2, Network, Lock,
  ExternalLink, ChevronRight, ChevronDown, ChevronUp, RefreshCw,
  Info, Wifi, WifiOff, MapPin, Building, Calendar, Database,
  Activity, CheckCircle, Clock, XCircle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length < 2) return '';
  const code = countryCode.toUpperCase().slice(0, 2);
  try {
    const codePoints = [...code].map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch { return ''; }
};

const PortBadge = ({ port }) => {
  const colors = {
    80: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    443: 'bg-green-500/20 text-green-300 border-green-500/30',
    22: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    21: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    25: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    3389: 'bg-red-500/20 text-red-300 border-red-500/30',
    8080: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    8443: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
  };
  const label = { 80: 'HTTP', 443: 'HTTPS', 22: 'SSH', 21: 'FTP', 25: 'SMTP', 3389: 'RDP', 8080: 'HTTP*', 8443: 'HTTPS*' };
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-mono border ${colors[port] || 'bg-gray-700 text-gray-300 border-gray-600'}`}>
      {port}{label[port] ? `/${label[port]}` : ''}
    </span>
  );
};

const StatusBadge = ({ http, https }) => {
  const code = https || http;
  if (!code) return <span className="text-gray-600 text-xs">—</span>;
  const color = code < 300 ? 'text-green-400' : code < 400 ? 'text-yellow-400' : 'text-red-400';
  return <span className={`text-xs font-mono ${color}`}>{https ? 'HTTPS' : 'HTTP'} {code}</span>;
};

const ScanStatusIcon = ({ status }) => {
  if (status === 'completed') return <CheckCircle className="w-3.5 h-3.5 text-green-400" />;
  if (status === 'running') return <Activity className="w-3.5 h-3.5 text-blue-400 animate-pulse" />;
  if (status === 'failed') return <XCircle className="w-3.5 h-3.5 text-red-400" />;
  if (status === 'pending') return <Clock className="w-3.5 h-3.5 text-yellow-400" />;
  return <Clock className="w-3.5 h-3.5 text-gray-500" />;
};

const DomainCartography = ({ scanId, domain }) => {
  const [loading, setLoading] = useState(true);
  const [subdomains, setSubdomains] = useState([]);
  const [enriched, setEnriched] = useState({}); // domain -> enrichment data
  const [loadingEnrich, setLoadingEnrich] = useState({});
  const [hosts, setHosts] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [vpsInfo, setVpsInfo] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);
  const [mainEnrich, setMainEnrich] = useState(null);
  const [childScans, setChildScans] = useState([]);

  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchEnrich = useCallback(async (d) => {
    if (enriched[d] || loadingEnrich[d]) return;
    setLoadingEnrich(prev => ({ ...prev, [d]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/v1/subdomains/enrich/${encodeURIComponent(d)}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEnriched(prev => ({ ...prev, [d]: data }));
      }
    } catch (e) {
      console.warn('Enrich failed:', d, e);
    } finally {
      setLoadingEnrich(prev => ({ ...prev, [d]: false }));
    }
  }, [enriched, loadingEnrich]);

  useEffect(() => {
    const fetchData = async () => {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!scanId || !uuidRegex.test(scanId)) { setLoading(false); return; }

      try {
        setLoading(true);

        // Fetch hosts from scan
        const hostsRes = await fetch(`${API_BASE}/api/v1/scan/${scanId}/hosts`, { headers });
        if (hostsRes.ok) setHosts(await hostsRes.json() || []);

        // Fetch vulnerabilities from scan
        try {
          const vulnsRes = await fetch(`${API_BASE}/api/v1/scan/${scanId}/vulnerabilities`, { headers });
          if (vulnsRes.ok) setVulnerabilities(await vulnsRes.json() || []);
        } catch {}

        // Fetch VPS info
        try {
          const vpsRes = await fetch(`${API_BASE}/api/v1/scan/${scanId}/vps-info`, { headers });
          if (vpsRes.ok) setVpsInfo(await vpsRes.json());
        } catch {}

        // Fetch subdomains (from scan store first, else quick discovery)
        let subs = [];
        let alreadyEnriched = false;
        try {
          const subRes = await fetch(`${API_BASE}/api/v1/subdomains/scan/${scanId}`, { headers });
          if (subRes.ok) {
            const subData = await subRes.json();
            subs = subData.subdomains || [];
            alreadyEnriched = subData.enriched || false;
          }
        } catch {}

        // If no scan subdomains yet, use quick discovery
        if (subs.length === 0 && domain) {
          try {
            const cleanDomain = domain.replace(/^https?:\/\//, '').split('/')[0];
            const quickRes = await fetch(`${API_BASE}/api/v1/subdomains/quick/${cleanDomain}`, { headers });
            if (quickRes.ok) {
              const qData = await quickRes.json();
              subs = (qData.alive_subdomains || []).map(s => ({
                subdomain: s.subdomain,
                ip: s.ip,
                alive: s.alive ?? true
              }));
            }
          } catch {}
        }

        setSubdomains(subs);

        // Pre-populate enriched state from subdomains that already have stored enrichment
        if (subs.length > 0) {
          const preEnriched = {};
          subs.forEach(s => {
            const name = s.subdomain || s.name || s.hostname || '';
            if (name && (s.open_ports || s.registrar || s.org || s.country)) {
              preEnriched[name] = {
                ip: s.ip,
                country: s.country,
                org: s.org,
                registrar: s.registrar,
                creation_date: s.creation_date,
                expiration_date: s.expiration_date,
                nameservers: s.nameservers,
                open_ports: s.open_ports,
                http_status: s.http_status,
                https_status: s.https_status,
                cdn: s.cdn,
              };
            }
          });
          if (Object.keys(preEnriched).length > 0) {
            setEnriched(prev => ({ ...prev, ...preEnriched }));
          }
        }

        // Fetch child scans (subdomain auto-scans)
        try {
          const childRes = await fetch(`${API_BASE}/api/v1/subdomains/child-scans/${scanId}`, { headers });
          if (childRes.ok) {
            const childData = await childRes.json();
            setChildScans(childData.child_scans || []);
          }
        } catch {}

        setLoading(false);

        // Auto-enrich main domain
        if (domain) {
          const cleanDomain = domain.replace(/^https?:\/\//, '').split('/')[0];
          fetchEnrich(cleanDomain);
        }

        // Auto-enrich all alive subdomains via bulk endpoint (saves to DB)
        if (subs.length > 0 && !alreadyEnriched) {
          try {
            const enrichAllRes = await fetch(
              `${API_BASE}/api/v1/subdomains/enrich-all/${scanId}`,
              { method: 'POST', headers }
            );
            if (enrichAllRes.ok) {
              const enrichAllData = await enrichAllRes.json();
              // Update enriched state with all returned data
              const newEnriched = {};
              (enrichAllData.subdomains || []).forEach(s => {
                const name = s.subdomain || s.name || '';
                if (name && (s.open_ports || s.registrar || s.org || s.country)) {
                  newEnriched[name] = {
                    ip: s.ip,
                    country: s.country,
                    org: s.org,
                    registrar: s.registrar,
                    creation_date: s.creation_date,
                    expiration_date: s.expiration_date,
                    nameservers: s.nameservers,
                    open_ports: s.open_ports,
                    http_status: s.http_status,
                    https_status: s.https_status,
                    cdn: s.cdn,
                  };
                }
              });
              if (Object.keys(newEnriched).length > 0) {
                setEnriched(prev => ({ ...prev, ...newEnriched }));
              }
            }
          } catch (e) {
            console.warn('Bulk enrich failed, falling back to on-demand enrichment', e);
          }
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (scanId) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [scanId, domain]);

  // Vulns by severity
  const vulnsBySeverity = vulnerabilities.reduce((acc, v) => {
    const s = v.severity || 'info';
    acc[s] = (acc[s] || []);
    acc[s].push(v);
    return acc;
  }, {});

  // Ports from hosts
  const allPorts = new Set();
  hosts.forEach(h => (h.ports || []).forEach(p => allPorts.add(p.port || p)));

  // Alive subdomains
  const aliveSubs = subdomains.filter(s => s.alive || s.ip);
  const cleanDomain = domain ? domain.replace(/^https?:\/\//, '').split('/')[0] : '';

  // Total unique IPs
  const hostIPs = new Set([
    ...hosts.map(h => h.ip_address || h.ip).filter(Boolean),
    ...aliveSubs.map(s => s.ip).filter(Boolean)
  ]);

  if (loading && hosts.length === 0 && subdomains.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-brand-green mx-auto mb-3 animate-spin" />
          <p className="text-gray-400">Analyse de la surface d'attaque...</p>
        </div>
      </div>
    );
  }

  const mainEnrichData = enriched[cleanDomain];

  return (
    <div className="p-4 space-y-4">

      {/* ── Stats summary ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Hosts', value: hostIPs.size || hosts.length, color: 'text-brand-green', sub: subdomains.length > 0 ? `${subdomains.length} subdom.` : null },
          { label: 'Ports ouverts', value: allPorts.size + (mainEnrichData?.open_ports?.length || 0), color: 'text-cyan-400' },
          { label: 'Vulnerabilities', value: vulnerabilities.length, color: 'text-red-400' },
          { label: 'Critiques/High', value: (vulnsBySeverity.critical || []).length + (vulnsBySeverity.high || []).length, color: 'text-orange-400' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-gray-900/60 rounded-lg p-3 text-center border border-gray-800">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            {sub && <p className="text-xs text-gray-700 mt-0.5">{sub}</p>}
          </div>
        ))}
      </div>

      {/* ── Main domain card ── */}
      <div className="bg-gray-900/60 rounded-xl border border-brand-green/30 overflow-hidden">
        <div className="p-3 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-brand-green" />
            <span className="font-bold text-brand-green font-mono">{cleanDomain}</span>
            <span className="text-xs text-gray-500">— Domaine principal</span>
          </div>
          {loadingEnrich[cleanDomain] && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
        </div>
        {mainEnrichData ? (
          <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-gray-500 mb-1 flex items-center gap-1"><Server className="w-3 h-3" /> IP</p>
              <p className="font-mono text-white">{mainEnrichData.ip || '—'}</p>
              {mainEnrichData.cdn && <span className="text-orange-400 text-xs">{mainEnrichData.cdn}</span>}
            </div>
            <div>
              <p className="text-gray-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Pays / Org</p>
              <p className="text-white">{getCountryFlag(mainEnrichData.country)} {mainEnrichData.country || '—'}</p>
              <p className="text-gray-400 truncate">{mainEnrichData.org || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1 flex items-center gap-1"><Building className="w-3 h-3" /> Registrar</p>
              <p className="text-white truncate">{mainEnrichData.registrar || '—'}</p>
              {mainEnrichData.creation_date && (
                <p className="text-gray-400"><Calendar className="w-3 h-3 inline" /> {mainEnrichData.creation_date}</p>
              )}
            </div>
            <div>
              <p className="text-gray-500 mb-1 flex items-center gap-1"><Network className="w-3 h-3" /> Ports / HTTP</p>
              <div className="flex flex-wrap gap-1 mb-1">
                {(mainEnrichData.open_ports || []).map(p => <PortBadge key={p} port={p} />)}
                {(mainEnrichData.open_ports || []).length === 0 && <span className="text-gray-600">aucun</span>}
              </div>
              <StatusBadge http={mainEnrichData.http_status} https={mainEnrichData.https_status} />
            </div>
            {mainEnrichData.nameservers?.length > 0 && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-gray-500 mb-1 text-xs">Nameservers</p>
                <div className="flex flex-wrap gap-2">
                  {mainEnrichData.nameservers.map((ns, i) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-800 rounded text-xs font-mono text-gray-300">{ns}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-3 text-xs text-gray-600 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Chargement WHOIS / IP...
          </div>
        )}
      </div>

      {/* ── Subdomains table ── */}
      <div className="bg-gray-900/60 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-3 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-cyan-400" />
            <span className="font-semibold text-white text-sm">
              Sous-domaines
              {subdomains.length > 0 && (
                <span className="ml-2 text-xs text-gray-400">
                  {subdomains.length} discovered — {aliveSubs.length} active
                </span>
              )}
            </span>
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
        </div>

        {subdomains.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Network className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Subdomain discovery in progress...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500">
                  <th className="text-left p-2 pl-3">Sous-domaine</th>
                  <th className="text-left p-2">IP</th>
                  <th className="text-left p-2">Pays / Org</th>
                  <th className="text-left p-2">Ports</th>
                  <th className="text-left p-2">HTTP</th>
                  <th className="text-left p-2">Statut</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {subdomains.map((sub, idx) => {
                  const name = sub.subdomain || sub.name || sub.hostname || '';
                  const ip = sub.ip || sub.ip_address || '';
                  const alive = sub.alive || !!ip;
                  const e = enriched[name];
                  const isExpanded = expanded[name];
                  return (
                    <React.Fragment key={idx}>
                      <tr className={`border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors ${!alive ? 'opacity-50' : ''}`}>
                        <td className="p-2 pl-3">
                          <div className="flex items-center gap-1.5">
                            {alive
                              ? <Wifi className="w-3 h-3 text-green-400 flex-shrink-0" />
                              : <WifiOff className="w-3 h-3 text-gray-600 flex-shrink-0" />
                            }
                            <span className="font-mono text-gray-200">{name}</span>
                          </div>
                        </td>
                        <td className="p-2 font-mono text-gray-300">{e?.ip || ip || '—'}</td>
                        <td className="p-2">
                          {e ? (
                            <span className="text-gray-300">
                              {getCountryFlag(e.country)} {e.country || '—'}
                              {e.cdn && <span className="ml-1 text-orange-400 text-xs">{e.cdn}</span>}
                            </span>
                          ) : ip ? (
                            <span className="text-gray-600">—</span>
                          ) : <span className="text-gray-700">—</span>}
                        </td>
                        <td className="p-2">
                          {e?.open_ports?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {e.open_ports.slice(0, 4).map(p => <PortBadge key={p} port={p} />)}
                              {e.open_ports.length > 4 && <span className="text-gray-500">+{e.open_ports.length - 4}</span>}
                            </div>
                          ) : e ? <span className="text-gray-600 text-xs">aucun</span> : <span className="text-gray-700">—</span>}
                        </td>
                        <td className="p-2">
                          {e ? <StatusBadge http={e.http_status} https={e.https_status} /> : <span className="text-gray-700">—</span>}
                        </td>
                        <td className="p-2">
                          {alive && (
                            <span className={`px-1.5 py-0.5 rounded text-xs ${alive ? 'bg-green-500/10 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                              {alive ? 'actif' : 'off'}
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => {
                              setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
                              if (!e && !loadingEnrich[name]) fetchEnrich(name);
                            }}
                            className="text-gray-600 hover:text-gray-300 transition-colors p-0.5"
                          >
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-800/50 bg-gray-800/20">
                          <td colSpan="7" className="px-4 py-3">
                            {loadingEnrich[name] ? (
                              <div className="flex items-center gap-2 text-gray-500 text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" /> Enrichissement WHOIS / ports...
                              </div>
                            ) : e ? (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                <div>
                                  <p className="text-gray-500 mb-1">Registrar</p>
                                  <p className="text-gray-300">{e.registrar || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500 mb-1">Organisation</p>
                                  <p className="text-gray-300">{e.org || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500 mb-1">Created</p>
                                  <p className="text-gray-300">{e.creation_date || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500 mb-1">Expiration</p>
                                  <p className="text-gray-300">{e.expiration_date || '—'}</p>
                                </div>
                                {e.nameservers?.length > 0 && (
                                  <div className="col-span-2 md:col-span-4">
                                    <p className="text-gray-500 mb-1">Nameservers</p>
                                    <div className="flex flex-wrap gap-1">
                                      {e.nameservers.map((ns, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-gray-900 rounded font-mono text-gray-400">{ns}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div className="col-span-2 md:col-span-4">
                                  <p className="text-gray-500 mb-1">Tous les ports</p>
                                  <div className="flex flex-wrap gap-1">
                                    {(e.open_ports || []).map(p => <PortBadge key={p} port={p} />)}
                                    {(e.open_ports || []).length === 0 && <span className="text-gray-600">No open ports detected</span>}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-600 text-xs">Cliquer pour enrichir...</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Child scans (auto subdomain scans) ── */}
      {childScans.length > 0 && (
        <div className="bg-gray-900/60 rounded-xl border border-blue-500/20 overflow-hidden">
          <div className="p-3 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <span className="font-semibold text-white text-sm">
                Scans automatiques des sous-domaines
                <span className="ml-2 text-xs text-gray-400">{childScans.length} scans</span>
              </span>
            </div>
            {childScans.some(s => s.status === 'running') && (
              <span className="text-xs text-blue-400 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> En cours
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500">
                  <th className="text-left p-2 pl-3">Cible</th>
                  <th className="text-left p-2">Statut</th>
                  <th className="text-left p-2">Progression</th>
                  <th className="text-left p-2">Hosts</th>
                  <th className="text-left p-2">Ports</th>
                  <th className="text-left p-2">Vulnerabilities</th>
                  <th className="text-left p-2">Crit/High</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {childScans.map((cs) => (
                  <tr key={cs.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="p-2 pl-3">
                      <span className="font-mono text-gray-200">{cs.target}</span>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1.5">
                        <ScanStatusIcon status={cs.status} />
                        <span className={`capitalize ${
                          cs.status === 'completed' ? 'text-green-400' :
                          cs.status === 'running' ? 'text-blue-400' :
                          cs.status === 'failed' ? 'text-red-400' :
                          'text-gray-400'
                        }`}>{cs.status}</span>
                      </div>
                    </td>
                    <td className="p-2">
                      {cs.status === 'running' ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-700 rounded-full h-1.5 w-16">
                            <div className="bg-blue-400 h-1.5 rounded-full" style={{ width: `${cs.progress}%` }} />
                          </div>
                          <span className="text-gray-400">{cs.progress}%</span>
                        </div>
                      ) : cs.status === 'completed' ? (
                        <span className="text-green-400">100%</span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="p-2 text-gray-300">{cs.total_hosts_found || '—'}</td>
                    <td className="p-2 text-gray-300">{cs.total_ports || '—'}</td>
                    <td className="p-2 text-gray-300">{cs.total_vulnerabilities || '—'}</td>
                    <td className="p-2">
                      {(cs.critical_count > 0 || cs.high_count > 0) ? (
                        <span className="text-red-400 font-semibold">
                          {cs.critical_count + cs.high_count}
                        </span>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="p-2">
                      <a
                        href={`/scans/${cs.id}`}
                        className="text-gray-500 hover:text-blue-400 transition-colors"
                        title="Voir le scan"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── VPS / Network ── */}
      {vpsInfo && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-300">Infrastructure de scan</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-gray-500">IP sortie VPN</p>
              <p className="font-mono text-blue-300">{vpsInfo?.vps?.vpn_public_ip || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Pays</p>
              <p className="text-blue-300">{getCountryFlag(vpsInfo?.vps?.vpn_country_code || 'FR')} {vpsInfo?.vps?.vpn_country || 'France'}</p>
            </div>
            <div>
              <p className="text-gray-500">VPN</p>
              <p className="text-blue-300">{vpsInfo?.vps?.vpn_provider || 'Ikoula'}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Vulnerabilities summary ── */}
      {vulnerabilities.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-red-400" />
            <span className="text-sm font-semibold text-red-300">{vulnerabilities.length} vulnerabilities detected</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {['critical', 'high', 'medium', 'low', 'info'].map(sev => {
              const count = (vulnsBySeverity[sev] || []).length;
              if (!count) return null;
              const colors = { critical: 'bg-red-500/20 text-red-300', high: 'bg-orange-500/20 text-orange-300', medium: 'bg-yellow-500/20 text-yellow-300', low: 'bg-blue-500/20 text-blue-300', info: 'bg-gray-700 text-gray-400' };
              return (
                <span key={sev} className={`px-2 py-1 rounded text-xs font-semibold ${colors[sev]}`}>
                  {count} {sev.toUpperCase()}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainCartography;
